import { c as _c } from "react/compiler-runtime";
import { useState, useRef } from 'react';
import { useField } from 'formik';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { useSelect } from 'downshift';
import _orderBy from 'lodash.orderby';
import ChoiceGroup from '@helios/ChoiceGroup';
import PhoneField from '@helios/PhoneField';
import SelectField from '@helios/SelectField';
import TextArea from '@helios/TextArea';
import TextField from '@helios/TextField';
import SolidChevronDownIcon from '@/components/icons/solid/chevron-down';
import Cleave from './Cleave.js';
import countryFlags from '@/utils/countryFlags.json';
import styles from './Field.module.scss';

// The legacy textarea was 160px tall; at the Helios field's 24px line and
// 12px padding, six rows is the nearest height that does not shrink it. A
// caller's own `rows` wins.
const TEXTAREA_ROWS = 6;

/**
 * The app's Formik field (Phase 5 PR 5.1): one `useField` binding rendered
 * on the Helios form controls. Formik, formatting (Cleave) and the country
 * picker stay here; Helios stays presentational. The invalid state is
 * `meta.touched && meta.error`: it sets `aria-invalid` on the control (the
 * controls draw their error edge from it). The label and the error text are
 * `components/FieldGroup.js`'s job.
 */
function Field(t0) {
  const $ = _c(43);
  let as;
  let children;
  let formatOptions;
  let icon;
  let label;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      label,
      icon,
      formatOptions,
      emptyOptionLabel: t1,
      showCountrySelect: t2,
      as,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = as;
    $[2] = children;
    $[3] = formatOptions;
    $[4] = icon;
    $[5] = label;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    as = $[1];
    children = $[2];
    formatOptions = $[3];
    icon = $[4];
    label = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const emptyOptionLabel = t1 === undefined ? "Choose..." : t1;
  const showCountrySelect = t2 === undefined ? false : t2;
  let t3;
  if ($[9] !== as || $[10] !== props) {
    t3 = {
      ...props,
      as
    };
    $[9] = as;
    $[10] = props;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const [field, meta] = useField(t3);
  const fieldValue = field.value ?? "";
  const ariaInvalid = props["aria-invalid"] ?? (meta.error && meta.touched ? true : undefined);
  if (as === "select") {
    const t4 = fieldValue === "" ? emptyOptionLabel : undefined;
    let t5;
    if ($[12] !== ariaInvalid || $[13] !== children || $[14] !== field || $[15] !== fieldValue || $[16] !== props || $[17] !== t4) {
      t5 = <SelectField {...field} {...props} value={fieldValue} aria-invalid={ariaInvalid} placeholderOption={t4}>{children}</SelectField>;
      $[12] = ariaInvalid;
      $[13] = children;
      $[14] = field;
      $[15] = fieldValue;
      $[16] = props;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    return t5;
  } else {
    if (props.type === "radio" || props.type === "checkbox") {
      const t4 = props.type === "checkbox" ? icon : undefined;
      let t5;
      if ($[19] !== ariaInvalid || $[20] !== field || $[21] !== label || $[22] !== props || $[23] !== t4) {
        t5 = <ChoiceGroup.Option {...field} {...props} type={props.type} aria-invalid={ariaInvalid} label={label} icon={t4} />;
        $[19] = ariaInvalid;
        $[20] = field;
        $[21] = label;
        $[22] = props;
        $[23] = t4;
        $[24] = t5;
      } else {
        t5 = $[24];
      }
      return t5;
    } else {
      if (formatOptions) {
        if (formatOptions.phone === true) {
          let t4;
          if ($[25] !== ariaInvalid || $[26] !== formatOptions || $[27] !== props || $[28] !== showCountrySelect) {
            t4 = <PhoneInput {...props} aria-invalid={ariaInvalid} showCountrySelect={showCountrySelect} formatOptions={formatOptions} />;
            $[25] = ariaInvalid;
            $[26] = formatOptions;
            $[27] = props;
            $[28] = showCountrySelect;
            $[29] = t4;
          } else {
            t4 = $[29];
          }
          return t4;
        }
        let t4;
        if ($[30] !== ariaInvalid || $[31] !== field || $[32] !== formatOptions || $[33] !== props) {
          t4 = <FormattedInput {...field} {...props} aria-invalid={ariaInvalid} formatOptions={formatOptions} />;
          $[30] = ariaInvalid;
          $[31] = field;
          $[32] = formatOptions;
          $[33] = props;
          $[34] = t4;
        } else {
          t4 = $[34];
        }
        return t4;
      } else {
        if (props.type === "textarea") {
          let t4;
          if ($[35] !== ariaInvalid || $[36] !== field || $[37] !== props) {
            t4 = <TextArea rows={TEXTAREA_ROWS} {...field} {...props} aria-invalid={ariaInvalid} />;
            $[35] = ariaInvalid;
            $[36] = field;
            $[37] = props;
            $[38] = t4;
          } else {
            t4 = $[38];
          }
          return t4;
        }
      }
    }
  }
  let t4;
  if ($[39] !== ariaInvalid || $[40] !== field || $[41] !== props) {
    t4 = <TextField {...field} {...props} aria-invalid={ariaInvalid} />;
    $[39] = ariaInvalid;
    $[40] = field;
    $[41] = props;
    $[42] = t4;
  } else {
    t4 = $[42];
  }
  return t4;
}

/**
 * A Cleave-formatted TextField. Cleave is a class component that takes its
 * DOM node as `htmlRef`, an object ref (O138): Field gives it its own
 * `useRef` object and merges TextField's forwarded ref into it, and never
 * spreads renderInput's props onto Cleave.
 */
function FormattedInput(t0) {
  const $ = _c(9);
  let formatOptions;
  let props;
  if ($[0] !== t0) {
    ({
      formatOptions,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = formatOptions;
    $[2] = props;
  } else {
    formatOptions = $[1];
    props = $[2];
  }
  const htmlRef = useRef(null);
  let t1;
  if ($[3] !== formatOptions || $[4] !== props.type) {
    t1 = t2 => {
      const {
        ref,
        className,
        type: _type,
        ...inputProps
      } = t2;
      return <Cleave {...inputProps} {...legacyType(props.type)} htmlRef={mergeRefs(htmlRef, ref)} className={className} options={formatOptions} />;
    };
    $[3] = formatOptions;
    $[4] = props.type;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <TextField {...props} renderInput={t1} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/**
 * Cleave renders `type="text"` unless given a type: pass the caller's own
 * type only when it set one, so every formatted input keeps the type it
 * had (TextField would otherwise hand down `text`, or PhoneField `tel`).
 */
function legacyType(type) {
  return type === undefined ? {} : {
    type
  };
}

/**
 * Cleave sets `htmlRef.current` itself, so the merged ref is an object ref
 * whose setter also feeds the forwarded ref (a callback or an object).
 */
function mergeRefs(objectRef, forwardedRef) {
  return {
    get current() {
      return objectRef.current;
    },
    set current(node) {
      objectRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);else if (forwardedRef) forwardedRef.current = node;
    }
  };
}
const regionNames = new Intl.DisplayNames(['en'], {
  type: 'region'
});
const items = _orderBy(getCountries().map(key => ({
  code: key,
  emoji: countryFlags[key],
  name: regionNames.of(key),
  callingCode: `+${getCountryCallingCode(key)}`
})), [item => item.code === 'US' ? 0 : 1, item => item.name.normalize('NFD').toLowerCase()], ['asc', 'asc']);

/**
 * The country picker: downshift's select, kept as a button and a listbox
 * (a native select would show "🇺🇸 United States +1" where the button shows
 * "🇺🇸 +1"). It sits in PhoneField's `countryControl` slot.
 */
function PhoneCountrySelect(t0) {
  const $ = _c(31);
  const {
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = items.find(item => item.code === value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedItem = t1;
  let t2;
  if ($[2] !== onChange) {
    t2 = t3 => {
      const {
        selectedItem: selectedItem_0
      } = t3;
      if (selectedItem_0) {
        onChange(selectedItem_0.code);
      }
    };
    $[2] = onChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== selectedItem || $[5] !== t2) {
    t3 = {
      items,
      selectedItem,
      onSelectedItemChange: t2,
      itemToString: _temp
    };
    $[4] = selectedItem;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect(t3);
  let t4;
  if ($[7] !== getToggleButtonProps) {
    t4 = getToggleButtonProps();
    $[7] = getToggleButtonProps;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = selectedItem?.emoji;
  let t6;
  if ($[9] !== t5) {
    t6 = <small className={styles.country__flag}>{t5}</small>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = selectedItem?.callingCode;
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <span className={styles.country__value}>{t6}{t7}</span>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SolidChevronDownIcon className={styles.country__chevron} />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t4 || $[16] !== t8) {
    t10 = <button type="button" {...t4} className={styles.country__button}>{t8}{t9}</button>;
    $[15] = t4;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== getMenuProps) {
    t11 = getMenuProps();
    $[18] = getMenuProps;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = isOpen ? "true" : "false";
  let t13;
  if ($[20] !== getItemProps || $[21] !== highlightedIndex || $[22] !== isOpen) {
    t13 = isOpen && items.map((item_1, index) => <li key={item_1.code} {...getItemProps({
      item: item_1,
      index
    })} className={styles.country__option} data-highlighted={highlightedIndex === index ? "true" : "false"}><span className={styles.country__value}><small className={styles.country__flag}>{item_1.emoji}</small>{item_1.name}</span>{item_1.callingCode}</li>);
    $[20] = getItemProps;
    $[21] = highlightedIndex;
    $[22] = isOpen;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t12 || $[26] !== t13) {
    t14 = <ul {...t11} className={styles.country__menu} data-open={t12}>{t13}</ul>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t10 || $[29] !== t14) {
    t15 = <div className={styles.country}>{t10}{t14}</div>;
    $[28] = t10;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}

/**
 * The phone field: libphonenumber parses and formats, Cleave formats as the
 * user types. With the country picker it is a Helios PhoneField; without it
 * (LeadForm's US-only phone) a TextField `type="tel"`, both through
 * renderInput (O138).
 */
function _temp(item_0) {
  return item_0 ? `${item_0.emoji} ${item_0.name} ${item_0.callingCode}` : "";
}
function PhoneInput(t0) {
  const $ = _c(40);
  let formatOptions;
  let name;
  let placeholder;
  let props;
  let t1;
  if ($[0] !== t0) {
    const {
      name: t2,
      showCountrySelect: t3,
      formatOptions: t4,
      placeholder: t5,
      type: _type,
      ...t6
    } = t0;
    name = t2;
    t1 = t3;
    formatOptions = t4;
    placeholder = t5;
    props = t6;
    $[0] = t0;
    $[1] = formatOptions;
    $[2] = name;
    $[3] = placeholder;
    $[4] = props;
    $[5] = t1;
  } else {
    formatOptions = $[1];
    name = $[2];
    placeholder = $[3];
    props = $[4];
    t1 = $[5];
  }
  const showCountrySelect = t1 === undefined ? false : t1;
  const ref = useRef(null);
  const [field,, helpers] = useField(name);
  let t2;
  if ($[6] !== field.value || $[7] !== formatOptions?.phone) {
    t2 = formatOptions?.phone && field.value ? parsePhoneNumberFromString(field.value)?.country ?? null : null;
    $[6] = field.value;
    $[7] = formatOptions?.phone;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const [selectedCountry, setSelectedCountry] = useState(t2);
  const phoneRegionCode = selectedCountry ?? formatOptions?.phoneRegionCode ?? "US";
  let t3;
  if ($[9] !== formatOptions || $[10] !== phoneRegionCode || $[11] !== showCountrySelect) {
    t3 = showCountrySelect ? {
      ...formatOptions,
      phoneRegionCode
    } : formatOptions;
    $[9] = formatOptions;
    $[10] = phoneRegionCode;
    $[11] = showCountrySelect;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const updatedFormatOptions = t3;
  let t4;
  if ($[13] !== updatedFormatOptions) {
    t4 = t5 => {
      const {
        ref: forwardedRef,
        className,
        type: _type_0,
        ...inputProps
      } = t5;
      return <Cleave {...inputProps} htmlRef={mergeRefs(ref, forwardedRef)} className={className} options={updatedFormatOptions} />;
    };
    $[13] = updatedFormatOptions;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const renderInput = t4;
  let t5;
  if ($[15] !== field.value || $[16] !== phoneRegionCode) {
    t5 = parsePhoneNumberFromString(field.value, phoneRegionCode)?.formatNational() ?? field.value;
    $[15] = field.value;
    $[16] = phoneRegionCode;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== helpers || $[19] !== phoneRegionCode) {
    t6 = e => {
      const phoneNumber = parsePhoneNumberFromString(e.target.value, phoneRegionCode);
      helpers.setValue(phoneNumber?.number ?? e.target.value);
    };
    $[18] = helpers;
    $[19] = phoneRegionCode;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== field || $[22] !== placeholder || $[23] !== props || $[24] !== renderInput || $[25] !== t5 || $[26] !== t6) {
    t7 = {
      ...props,
      ...field,
      placeholder,
      value: t5,
      onChange: t6,
      renderInput
    };
    $[21] = field;
    $[22] = placeholder;
    $[23] = props;
    $[24] = renderInput;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const inputProps_0 = t7;
  if (!showCountrySelect) {
    let t8;
    if ($[28] !== inputProps_0) {
      t8 = <TextField {...inputProps_0} type="tel" />;
      $[28] = inputProps_0;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    return t8;
  }
  let t8;
  if ($[30] !== field.value || $[31] !== helpers || $[32] !== phoneRegionCode) {
    t8 = code => {
      let phoneNumber_0 = parsePhoneNumberFromString(field.value, phoneRegionCode);
      setSelectedCountry(code);
      if (phoneNumber_0?.nationalNumber) {
        phoneNumber_0 = parsePhoneNumberFromString(phoneNumber_0.nationalNumber, code);
        helpers.setValue(phoneNumber_0?.isPossible() ? phoneNumber_0.number : "");
      }
      ref.current?.focus();
    };
    $[30] = field.value;
    $[31] = helpers;
    $[32] = phoneRegionCode;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== phoneRegionCode || $[35] !== t8) {
    t9 = <PhoneCountrySelect value={phoneRegionCode} onChange={t8} />;
    $[34] = phoneRegionCode;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== inputProps_0 || $[38] !== t9) {
    t10 = <div className={styles.phone}><PhoneField {...inputProps_0} countryControl={t9} /></div>;
    $[37] = inputProps_0;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
}
export default Field;